// New-quote modal — fired from QuotesPage's "+ New quote" button (or from
// any caller that wants to seed a quote against a known customer, e.g.
// the customer detail page later). Two-step flow that mirrors
// NewPartModal's UX:
//
//   1. Pick customer — search (debounced 200ms) → scrollable result list
//      with TierBadge so Justin can eyeball pricing tier at-a-glance.
//      Skipped entirely when `initialCustomerId` is provided.
//   2. Basics — auto-generated quote number (override allowed), job type,
//      due date (default +21 days), and qty tiers (same parser as
//      NewPartModal).
//
// On submit: createQuote({ ... }) with all required Quote fields stamped
// to sensible draft defaults — empty parts list, zeroed totals/margins,
// approval not required, follow-up paused. Then onCreated(id) → caller
// routes to /quotes/:id where the actual building happens.

import { useEffect, useMemo, useRef, useState } from 'react';
import { createQuote, getCustomer, searchCustomers } from '@/shared/crm';
import type { Customer, JobType } from '@/shared/crmTypes';
import { NumberInput } from './NumberInput';
import { TierBadge } from './TierBadge';

export interface NewQuoteModalProps {
  open: boolean;
  onClose: () => void;
  onCreated?: (id: string) => void;
  /** Pre-select a customer — skips the customer-picker step. */
  initialCustomerId?: string;
}

const JOB_TYPES: Array<{ key: JobType; label: string; icon: string }> = [
  { key: 'multi_part_production', label: 'Multi-part production', icon: '⚙' },
  { key: 'prototype', label: 'Prototype', icon: '💡' },
  { key: 'one_off', label: 'One-off', icon: '🔩' },
  { key: 'quick_quote', label: 'Quick quote', icon: '⚡' },
  { key: 'spot_quote', label: 'Spot quote', icon: '🔍' },
  { key: 'welding_job', label: 'Welding job', icon: '🧯' },
  { key: 'machine_build', label: 'Machine build', icon: '🏭' },
];

// Quote shape — UI-only concept that picks sensible (qtyTiers, jobType)
// defaults. Under the hood it's just a pair of values; the actual quote
// builder treats every quote identically regardless of which shape was
// picked at create time.
type QuoteShape = 'single' | 'multi' | 'prototype';

const SHAPE_PILLS: Array<{
  key: QuoteShape;
  label: string;
  jobType: JobType;
}> = [
  { key: 'single', label: 'Single part', jobType: 'one_off' },
  { key: 'multi', label: 'Multi-tier production', jobType: 'multi_part_production' },
  { key: 'prototype', label: 'Prototype', jobType: 'prototype' },
];

const DEFAULT_QTY_TIERS = [10, 50, 100] as const;

/** Same parser shape as NewPartModal — keep them in sync if you change either. */
function parseQtyTiers(raw: string): number[] | null {
  const nums = raw
    .split(/[^\d]+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => Number.parseInt(s, 10));
  if (nums.length === 0 || nums.length > 5) return null;
  if (nums.some((n) => !Number.isFinite(n) || n <= 0)) return null;
  return nums;
}

/** Suggest a quote number — slice of Date.now() keeps it unique-ish for the
 *  draft phase; Justin can override before submit if his numbering scheme
 *  differs. Eventually this'll be replaced with a sequential generator. */
function suggestNumber(): string {
  return `Q-${Date.now().toString().slice(-5)}`;
}

/** ISO date for today + N days, formatted as yyyy-mm-dd for <input type="date">. */
function isoDatePlusDays(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export function NewQuoteModal({
  open,
  onClose,
  onCreated,
  initialCustomerId,
}: NewQuoteModalProps) {
  // ───── step state ─────
  const [step, setStep] = useState<'pickCustomer' | 'basics'>(
    initialCustomerId ? 'basics' : 'pickCustomer',
  );
  const [pickedCustomer, setPickedCustomer] = useState<Customer | null>(null);

  // ───── customer-picker state ─────
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [results, setResults] = useState<Customer[]>([]);
  const [searching, setSearching] = useState(false);

  // ───── basics state ─────
  const [number, setNumber] = useState('');
  const [shape, setShape] = useState<QuoteShape>('single');
  const [jobType, setJobType] = useState<JobType>('one_off');
  const [dueDate, setDueDate] = useState('');
  // Single-part qty (only used when shape === 'single' or 'prototype').
  const [singleQty, setSingleQty] = useState(1);
  // Multi-tier raw text (only used when shape === 'multi').
  const [qtyTiersRaw, setQtyTiersRaw] = useState(DEFAULT_QTY_TIERS.join(' / '));

  // ───── shared state ─────
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset whenever the modal (re)opens. If initialCustomerId is set we
  // eagerly fetch the customer record so step 2's header shows the name
  // and TierBadge instead of just the id.
  useEffect(() => {
    if (!open) return;
    setStep(initialCustomerId ? 'basics' : 'pickCustomer');
    setPickedCustomer(null);
    setSearchInput('');
    setSearchQuery('');
    setResults([]);
    setNumber(suggestNumber());
    setShape('single');
    setJobType('one_off');
    setDueDate(isoDatePlusDays(21));
    setSingleQty(1);
    setQtyTiersRaw(DEFAULT_QTY_TIERS.join(' / '));
    setError(null);
    setBusy(false);

    if (initialCustomerId) {
      // Fire and forget — failing this just leaves the header empty,
      // doesn't block the flow.
      void getCustomer(initialCustomerId).then((c) => {
        if (c) setPickedCustomer(c);
      });
    }
  }, [open, initialCustomerId]);

  // Close on Escape (mirrors NewPartModal contract).
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, busy, onClose]);

  // Debounce search input → searchQuery (same 200ms as QuotesPage).
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearchQuery(searchInput), 200);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  // Run the search whenever the debounced query (or the step) changes.
  // searchCustomers handles the empty-string case as "return everything",
  // which is exactly what we want as the initial state.
  useEffect(() => {
    if (!open || step !== 'pickCustomer') return;
    let cancelled = false;
    setSearching(true);
    searchCustomers(searchQuery)
      .then((list) => {
        if (!cancelled) setResults(list);
      })
      .catch(() => {
        // Silently drop — empty result list will read as "no matches".
        if (!cancelled) setResults([]);
      })
      .finally(() => {
        if (!cancelled) setSearching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, step, searchQuery]);

  const customerLabel = useMemo(() => {
    if (!pickedCustomer) return '';
    return pickedCustomer.companyName;
  }, [pickedCustomer]);

  if (!open) return null;

  function handlePickCustomer(c: Customer) {
    setPickedCustomer(c);
    setStep('basics');
  }

  function handleBackToPicker() {
    if (initialCustomerId) return; // no picker step when seeded
    setStep('pickCustomer');
  }

  // Pill click — flips shape and resets jobType to the pill's default.
  // User can still pick any of the 7 jobTypes from the select below.
  function handlePickShape(next: QuoteShape) {
    setShape(next);
    const pill = SHAPE_PILLS.find((p) => p.key === next);
    if (pill) setJobType(pill.jobType);
  }

  async function handleSubmit() {
    if (!pickedCustomer) {
      setError('Pick a customer first.');
      return;
    }
    if (!number.trim()) {
      setError('Quote number is required.');
      return;
    }
    // Derive tiers from shape: single/prototype → [qty]; multi → parsed list.
    let tiers: number[];
    if (shape === 'multi') {
      const parsed = parseQtyTiers(qtyTiersRaw);
      if (!parsed) {
        setError('Qty tiers must be 1-5 positive integers (e.g. "10 / 50 / 100").');
        return;
      }
      tiers = parsed;
    } else {
      if (!Number.isFinite(singleQty) || singleQty <= 0) {
        setError('Quantity must be a positive integer.');
        return;
      }
      tiers = [Math.floor(singleQty)];
    }

    setBusy(true);
    setError(null);
    try {
      const id = await createQuote({
        number: number.trim(),
        customerId: pickedCustomer.id,
        customerNameSnapshot: pickedCustomer.companyName,
        dueDate: dueDate || undefined,
        jobType,
        partIds: [],
        partQuotedSpecs: {},
        qtyTiers: tiers,
        status: 'draft',
        imported: false,
        // Zero-filled to match the tiers length — saves the builder a
        // recompute on first open and lets the totals card render
        // immediately instead of flashing a "—".
        totalsByTier: new Array<number>(tiers.length).fill(0),
        marginsByTier: new Array<number>(tiers.length).fill(0),
        marginWarning: false,
        approvalRequired: false,
        approvalStatus: 'na',
        revisions: [],
        followUp: {
          // Follow-up cadence kicks in once the quote is sent. Until then
          // we hold it paused so the nudge worker won't pick it up.
          stage: 'paused',
          draftsHistory: [],
          paused: true,
        },
      });
      onCreated?.(id);
      onClose();
    } catch (e) {
      setError(`Failed to create quote: ${(e as Error).message}`);
      setBusy(false);
    }
  }

  // ===== STEP 1: pick customer =====
  if (step === 'pickCustomer') {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-navy/50 p-4"
        onClick={() => !busy && onClose()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="new-quote-pick-title"
      >
        <div
          className="bg-paper rounded shadow-lg w-full max-w-[520px] max-h-[90vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-5 py-3 border-b border-line flex items-center justify-between bg-navy text-paper shrink-0">
            <h2 id="new-quote-pick-title" className="font-bold text-base">
              Pick a customer
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="text-paper/80 hover:text-paper text-lg leading-none"
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          <div className="p-4 shrink-0">
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by company name or code…"
              autoFocus
              className="w-full px-3 py-2 border border-line-dark rounded text-sm focus:outline-none focus:border-orange"
            />
            <div className="text-[11px] text-text-mute mt-1.5">
              {searching ? 'Searching…' : `${results.length} customer${results.length === 1 ? '' : 's'}`}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-4 pb-4">
            {results.length === 0 && !searching && (
              <div className="text-sm text-text-mute italic text-center py-6">
                {searchInput.trim()
                  ? `No customers match "${searchInput}".`
                  : 'No customers yet — add one from the Customers page first.'}
              </div>
            )}
            <ul className="flex flex-col gap-1.5">
              {results.map((c) => {
                const primary = c.contacts?.find((x) => x.primary) ?? c.contacts?.[0];
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => handlePickCustomer(c)}
                      className="w-full text-left border border-line rounded p-2 hover:border-orange hover:shadow-sm transition-all bg-paper flex items-center gap-3"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-navy truncate">
                            {c.companyName}
                          </span>
                          {c.code && (
                            <span className="text-[10px] font-mono text-text-mute">
                              {c.code}
                            </span>
                          )}
                        </div>
                        {primary && (
                          <div className="text-[11px] text-text-mute truncate mt-0.5">
                            {primary.name}
                            {primary.email && ` · ${primary.email}`}
                          </div>
                        )}
                      </div>
                      <TierBadge tier={c.tier} size="sm" />
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    );
  }

  // ===== STEP 2: basics =====
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-navy/50 p-4"
      onClick={() => !busy && onClose()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="new-quote-basics-title"
    >
      <div
        className="bg-paper rounded shadow-lg max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-line flex items-center justify-between">
          <h2 id="new-quote-basics-title" className="font-bold text-navy text-lg">
            + New quote
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="text-text-mute hover:text-navy disabled:opacity-50"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {pickedCustomer && (
          <div className="flex items-center gap-3 mx-5 mt-4 border border-line rounded px-3 py-2 bg-bg/40">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[13px] font-bold text-navy truncate">
                  {customerLabel}
                </span>
                <TierBadge tier={pickedCustomer.tier} size="sm" />
              </div>
              <div className="text-[11px] text-text-mute mt-0.5">
                Terms: {pickedCustomer.terms}
                {pickedCustomer.rep && ` · Rep: ${pickedCustomer.rep}`}
              </div>
            </div>
            {!initialCustomerId && (
              <button
                type="button"
                onClick={handleBackToPicker}
                disabled={busy}
                className="text-[11px] text-orange hover:underline disabled:opacity-50 shrink-0"
              >
                change
              </button>
            )}
          </div>
        )}

        <div className="p-5 space-y-3">
          <Field label="What kind of quote?">
            <div className="flex gap-1.5">
              {SHAPE_PILLS.map((p) => {
                const active = shape === p.key;
                return (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => handlePickShape(p.key)}
                    className={`flex-1 px-3 py-1.5 text-xs font-semibold rounded border transition-colors ${
                      active
                        ? 'bg-orange text-white border-orange'
                        : 'bg-paper text-text border-line-dark hover:border-orange'
                    }`}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
            <span className="block text-[11px] text-text-mute mt-1">
              {shape === 'single' && 'One part, qty 1. Switch to multi-tier for production batch pricing.'}
              {shape === 'multi' && 'Production batch with tiered quantity breaks.'}
              {shape === 'prototype' && 'One part, tagged as a prototype for tracking.'}
            </span>
          </Field>

          <Field label="Quote number *">
            <input
              type="text"
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              placeholder="Q-12345"
              autoFocus
              className="w-full px-2 py-1.5 border border-line-dark rounded text-sm font-mono focus:outline-none focus:border-orange"
            />
          </Field>

          {shape === 'multi' ? (
            <Field label="Qty tiers">
              <input
                type="text"
                value={qtyTiersRaw}
                onChange={(e) => setQtyTiersRaw(e.target.value)}
                placeholder="10 / 50 / 100"
                className="w-full px-2 py-1.5 border border-line-dark rounded text-sm font-mono focus:outline-none focus:border-orange"
              />
              <span className="block text-[11px] text-text-mute mt-1">
                1-5 positive integers, separated by slashes, commas, or spaces.
              </span>
            </Field>
          ) : (
            <Field label="Quantity">
              <NumberInput
                value={singleQty}
                onChange={(v) => setSingleQty(v === undefined ? 0 : Math.floor(v))}
                step={1}
                min={1}
                numpadTitle="Quantity"
              />
              <span className="block text-[11px] text-text-mute mt-1">
                Defaults to 1. Bump it up if you need a small run at this price.
              </span>
            </Field>
          )}

          <Field label="Job type">
            <select
              value={jobType}
              onChange={(e) => setJobType(e.target.value as JobType)}
              className="w-full px-2 py-1.5 border border-line-dark rounded text-sm bg-paper focus:outline-none focus:border-orange"
            >
              {JOB_TYPES.map((j) => (
                <option key={j.key} value={j.key}>
                  {j.icon} {j.label}
                </option>
              ))}
            </select>
            <span className="block text-[11px] text-text-mute mt-1">
              Auto-set from the shape pill above. Override if you need a different category.
            </span>
          </Field>

          <Field label="Due date">
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-full px-2 py-1.5 border border-line-dark rounded text-sm focus:outline-none focus:border-orange"
            />
            <span className="block text-[11px] text-text-mute mt-1">
              When the customer needs it by. Default is 21 days out.
            </span>
          </Field>

          {error && (
            <div className="bg-orange/10 border-l-4 border-orange text-text px-3 py-2 text-sm">
              ⚠ {error}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-line flex items-center justify-end gap-2 bg-bg/50">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-3 py-1.5 text-sm rounded border border-line-dark text-text hover:bg-bg disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={busy || !pickedCustomer || !number.trim()}
            className="px-4 py-1.5 bg-orange text-white text-sm rounded font-semibold disabled:opacity-50"
          >
            {busy ? 'Creating…' : 'Create quote'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface FieldProps {
  label: string;
  children: React.ReactNode;
}

function Field({ label, children }: FieldProps) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-text-mute uppercase tracking-wide mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}
